import i18next, { Resource, use } from 'i18next';
import _ from 'lodash';
import { initReactI18next } from 'react-i18next';
import { OperationTypes } from 'utils/enums/system-template';
import enTranslation from './en';
import esTranslation from './es-419';
import prTranslation from './pr';
import ruTranslation from './ru';
import heTranslation from './he';
import deTranslation from './de';
import { LanguagesCodes } from 'utils/enums/language';

type OperationTranslations = Partial<Record<OperationTypes, Record<string, unknown>>>;

type Translations = Record<
  string,
  {
    default: Record<string, unknown>;
    operations?: OperationTranslations;
  }
>;

const defaultOperation = import.meta.env.VITE_DEFAULT_OPERATION || 'default';

/**
 * New operation? New lanaguage? This is the place to configure it!
 * ⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇
 * ⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇⬇
 * */
const translations: Translations = {
  en: {
    default: enTranslation.default,
    operations: {
      [OperationTypes.School]: enTranslation.school,
      [OperationTypes.Deliveries]: enTranslation.deliveries,
      [OperationTypes.Employee]: enTranslation.employee,
      [OperationTypes.Broker]: enTranslation.broker,
    },
  },
  es: {
    default: esTranslation.default,
    operations: {
      [OperationTypes.School]: esTranslation.school,
      [OperationTypes.Deliveries]: esTranslation.deliveries,
      [OperationTypes.Employee]: esTranslation.employee,
      [OperationTypes.Broker]: esTranslation.broker,
    },
  },
  pr: {
    default: prTranslation.default,
    operations: {
      [OperationTypes.School]: prTranslation.school,
      [OperationTypes.Deliveries]: prTranslation.deliveries,
      [OperationTypes.Employee]: prTranslation.employee,
      [OperationTypes.Broker]: prTranslation.broker,
    },
  },
  [LanguagesCodes.He]: {
    default: heTranslation.default,
    operations: {
      [OperationTypes.School]: heTranslation.school,
      [OperationTypes.Deliveries]: heTranslation.deliveries,
      [OperationTypes.Employee]: heTranslation.employee,
      [OperationTypes.Broker]: heTranslation.broker,
    },
  },
  de: {
    default: deTranslation.default,
    operations: {
      [OperationTypes.School]: deTranslation.school,
      [OperationTypes.Deliveries]: deTranslation.deliveries,
      [OperationTypes.Employee]: deTranslation.employee,
      [OperationTypes.Broker]: deTranslation.broker,
    },
  },
  ru: {
    default: ruTranslation,
  },
};

const operationNamespaces = Object.values(OperationTypes).filter((operation) => operation !== OperationTypes.Default);

const resources: Resource = Object.entries(translations).reduce((acc: Resource, [language, specs]: [string, Translations[string]]) => {
  const operationResources = operationNamespaces.reduce<Record<string, Record<string, unknown>>>((acc, operation) => {
    acc[operation] = specs.operations?.[operation] || {};

    return acc;
  }, {});

  return {
    ...acc,
    [language]: {
      default: specs.default,
      ...operationResources,
    },
  };
}, {});

// passes i18n down to react-i18next
// eslint-disable-next-line react-hooks/rules-of-hooks
use(initReactI18next).init({
  resources,
  lng: import.meta.env.VITE_DEFAULT_LANAGUAGE || 'en',
  ns: [defaultOperation, ...operationNamespaces],
  defaultNS: defaultOperation,
  fallbackNS: defaultOperation,
  keySeparator: '.', // we do not use keys in form messages.welcome
  interpolation: { escapeValue: false }, // react already safes from xss
  fallbackLng: import.meta.env.VITE_DEFAULT_LANAGUAGE || 'en',
  parseMissingKeyHandler: (key: string, defaultValue?: string) => defaultValue || _.get(translations.en.default, key),
});

export default i18next;
